import type React from 'react'
import { useEffect } from 'react'
import { useIntl } from 'react-intl'

import { Navigate, useNavigate, useSearch } from '@tanstack/react-router'

import { useFirebaseUser } from '@/auth/useFirebaseUser'
import { type StatsSection, StatsView } from '@/components/templates/StatsView'

import { NOW_DATE, type SelectionMode, useStatsPage } from './useStatsPage'

// Defer navigation to allow dropdown animation to complete before React suspends
// requestAnimationFrame waits for next paint, then 50ms lets Radix close animation start
const deferNavigation = (doNavigate: () => void) => {
  requestAnimationFrame(() => {
    setTimeout(doNavigate, 50)
  })
}

export const StatsPage: React.FC = () => {
  const user = useFirebaseUser()
  const intl = useIntl()
  const {
    regionId,
    mode = 'manual',
    dates,
    fixedDay,
    weekday,
    referenceDate,
    section = 'requests',
  } = useSearch({
    from: '/stats/',
  })
  const navigate = useNavigate()
  const { availableRegions, availableDates, currentCampaignDates, selectedDates, chartData, isLoading, isFetching } =
    useStatsPage({
      regionId,
      mode,
      dates,
      fixedDay,
      weekday,
      referenceDate,
      locale: intl.locale,
      nowLabel: intl.formatMessage({ id: 'stats.now' }),
      nowSuffix: intl.formatMessage({ id: 'stats.nowSuffix' }),
    })

  // Default to "now" if no dates selected and there are operating regions,
  // otherwise default to the most recent closed evening (only for manual mode)
  const hasNowOption = availableDates.some((d) => d.date === NOW_DATE)
  const firstClosedDate = availableDates.find((d) => d.date !== NOW_DATE)?.date

  useEffect(() => {
    if (mode === 'manual' && dates.length === 0) {
      const defaultDate = hasNowOption ? NOW_DATE : firstClosedDate
      if (defaultDate) {
        navigate({
          to: '/stats',
          search: { regionId, mode, dates: defaultDate },
          replace: true,
        }).catch(() => {
          // Navigation errors are handled by the router
        })
      }
    }
  }, [mode, dates.length, hasNowOption, firstClosedDate, navigate, regionId])

  if (!user?.sys_admin) {
    return <Navigate to="/" search={{ central_id: undefined }} replace />
  }

  const handleRegionChange = (newRegionId: string | undefined) => {
    deferNavigation(() => {
      navigate({
        to: '/stats',
        search: {
          regionId: newRegionId,
          mode,
          dates: mode === 'manual' && dates.length > 0 ? dates.join(',') : undefined,
          fixedDay: mode === 'fixedDate' ? fixedDay : undefined,
          weekday: mode === 'weekday' ? weekday : undefined,
          referenceDate: mode === 'isoWeek' ? referenceDate : undefined,
        },
      }).catch(() => {
        // Navigation errors are handled by the router
      })
    })
  }

  const handleModeChange = (newMode: SelectionMode) => {
    deferNavigation(() => {
      // Clear mode-specific params when switching modes
      navigate({
        to: '/stats',
        search: {
          regionId,
          mode: newMode,
          // Reset all mode-specific params
          dates: undefined,
          fixedDay: undefined,
          weekday: undefined,
          referenceDate: undefined,
        },
      }).catch(() => {
        // Navigation errors are handled by the router
      })
    })
  }

  const handleDateToggle = (date: string) => {
    deferNavigation(() => {
      const newDates = dates.includes(date) ? dates.filter((d) => d !== date) : [...dates, date]
      navigate({
        to: '/stats',
        search: {
          regionId,
          mode,
          dates: newDates.length > 0 ? newDates.join(',') : undefined,
        },
      }).catch(() => {
        // Navigation errors are handled by the router
      })
    })
  }

  const handleClearDates = () => {
    deferNavigation(() => {
      navigate({
        to: '/stats',
        search: { regionId, mode, dates: undefined },
      }).catch(() => {
        // Navigation errors are handled by the router
      })
    })
  }

  const handleFixedDayChange = (day: number | undefined) => {
    deferNavigation(() => {
      navigate({
        to: '/stats',
        search: { regionId, mode, fixedDay: day },
      }).catch(() => {
        // Navigation errors are handled by the router
      })
    })
  }

  const handleWeekdayChange = (day: number | undefined) => {
    deferNavigation(() => {
      navigate({
        to: '/stats',
        search: { regionId, mode, weekday: day },
      }).catch(() => {
        // Navigation errors are handled by the router
      })
    })
  }

  const handleReferenceDateChange = (date: string | undefined) => {
    deferNavigation(() => {
      navigate({
        to: '/stats',
        search: { regionId, mode, referenceDate: date },
      }).catch(() => {
        // Navigation errors are handled by the router
      })
    })
  }

  const handleSectionChange = (newSection: StatsSection) => {
    deferNavigation(() => {
      navigate({
        to: '/stats',
        search: {
          section: newSection,
          regionId,
          // Preserve mode-specific params only when staying on requests section
          mode: newSection === 'requests' ? mode : undefined,
          dates: newSection === 'requests' && dates.length > 0 ? dates.join(',') : undefined,
          fixedDay: newSection === 'requests' ? fixedDay : undefined,
          weekday: newSection === 'requests' ? weekday : undefined,
          referenceDate: newSection === 'requests' ? referenceDate : undefined,
        },
      }).catch(() => {
        // Navigation errors are handled by the router
      })
    })
  }

  return (
    <StatsView
      availableRegions={availableRegions}
      availableDates={availableDates}
      currentCampaignDates={currentCampaignDates}
      selectedRegionId={regionId}
      selectedMode={mode}
      selectedDates={selectedDates}
      selectedFixedDay={fixedDay}
      selectedWeekday={weekday}
      selectedReferenceDate={referenceDate}
      chartData={chartData}
      isLoading={isLoading}
      isFetching={isFetching}
      selectedSection={section}
      onSectionChange={handleSectionChange}
      onRegionChange={handleRegionChange}
      onModeChange={handleModeChange}
      onDateToggle={handleDateToggle}
      onClearDates={handleClearDates}
      onFixedDayChange={handleFixedDayChange}
      onWeekdayChange={handleWeekdayChange}
      onReferenceDateChange={handleReferenceDateChange}
      onBackToPiloteClicked={async () => {
        await navigate({ to: '/', search: { central_id: undefined } })
      }}
    />
  )
}
